// 항목 관리: the items of the three dimensions (사람, 분류 in two levels,
// 결제수단): add, rename, delete, order (up and down) and move a category
// under another one. Rows keep pointing at a deleted item (its name stays).
// The dimensions people make (Dims.tsx, design 02 3.2) are tabs after them.
import { type FormEvent, useState } from 'react'

import { errorText } from '../../api/client'
import { ErrorText, Field, Icon, IconButton, Sheet } from '../../ui/common'
import { showToast } from '../../ui/toast'
import type { BuiltinRole, Id, TensorItem } from '../api'
import { engine } from '../engine'
import { NAME_MAX } from '../csv'
import { customDims } from '../dims'
import { itemsOf } from '../views'
import type { Current } from './common'
import { DimPanel, NewDimSheet } from './Dims'

const ROLES: { role: BuiltinRole; label: string }[] = [
  { role: 'person', label: '사람' },
  { role: 'category', label: '분류' },
  { role: 'payment', label: '결제수단' },
]

function cleanName(s: string): string | null {
  const t = s.trim().normalize('NFC')
  if (t === '' || Array.from(t).length > NAME_MAX || t.includes('>')) return null
  return t
}

async function run(fn: () => Promise<void>): Promise<boolean> {
  try {
    await fn()
    return true
  } catch (e) {
    showToast(errorText(e))
    return false
  }
}

export function ItemRow({
  cur,
  item,
  index,
  count,
  onEdit,
  sub,
}: {
  cur: Current
  item: TensorItem
  index: number
  count: number
  onEdit: (i: TensorItem) => void
  sub?: boolean
}) {
  const move = (to: number) =>
    void run(() => engine.updateItem(cur.workbook.id, item.id, { position: to, version: item.version }))
  return (
    <li className={`t-item${sub ? ' sub' : ''}`}>
      <span className="grow">{item.name}</span>
      <IconButton label={`${item.name} 위로`} disabled={index === 0} onClick={() => move(index - 1)}>
        <Icon name="up" />
      </IconButton>
      <IconButton label={`${item.name} 아래로`} disabled={index === count - 1} onClick={() => move(index + 1)}>
        <Icon name="down" />
      </IconButton>
      <IconButton label={`${item.name} 고치기`} onClick={() => onEdit(item)}>
        <Icon name="edit" />
      </IconButton>
    </li>
  )
}

export function ItemSheet({ cur, item, onClose }: { cur: Current; item: TensorItem | null; onClose: () => void }) {
  return (
    <Sheet open={item !== null} onClose={onClose} title="항목 고치기">
      {item && <ItemForm key={item.id} cur={cur} item={item} onDone={onClose} />}
    </Sheet>
  )
}

function ItemForm({ cur, item, onDone }: { cur: Current; item: TensorItem; onDone: () => void }) {
  const items = cur.book?.items ?? {}
  const [name, setName] = useState(item.name)
  const [parent, setParent] = useState<Id>(item.parent_id ?? 0)
  const [error, setError] = useState<string | null>(null)
  const hasChildren = itemsOf(items, 'category', item.id).length > 0
  const tops = itemsOf(items, 'category').filter((i) => i.id !== item.id)

  async function submit(e: FormEvent) {
    e.preventDefault()
    const n = cleanName(name)
    if (!n) {
      setError(`이름은 1~${NAME_MAX}자이고 '>'를 쓸 수 없습니다.`)
      return
    }
    const body: { name?: string; parent_id?: number; version: number } = { version: item.version }
    if (n !== item.name) body.name = n
    if (item.role === 'category' && parent !== (item.parent_id ?? 0)) body.parent_id = parent
    if (body.name === undefined && body.parent_id === undefined) return onDone()
    try {
      await engine.updateItem(cur.workbook.id, item.id, body)
      onDone()
    } catch (err) {
      setError(errorText(err))
    }
  }

  async function remove() {
    const what = hasChildren ? `${item.name}과 그 아래 분류를` : `${item.name}을(를)`
    if (!confirm(`${what} 지울까요? 이미 적은 거래는 그대로 이 이름을 보여 줍니다.`)) return
    if (await run(() => engine.deleteItem(cur.workbook.id, item.id))) onDone()
  }

  return (
    <form className="t-form" onSubmit={(e) => void submit(e)}>
      <Field label="이름">
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={NAME_MAX} required />
      </Field>
      {item.role === 'category' && !hasChildren && (
        <Field label="위 분류">
          <select value={parent} onChange={(e) => setParent(Number(e.target.value))}>
            <option value={0}>(맨 위)</option>
            {tops.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </Field>
      )}
      <ErrorText text={error} />
      <div className="t-buttons">
        <button type="button" className="danger-btn" onClick={() => void remove()}>
          지우기
        </button>
        <button className="primary grow" type="submit">
          저장
        </button>
      </div>
    </form>
  )
}

function AddItem({ cur, role }: { cur: Current; role: BuiltinRole }) {
  const items = cur.book?.items ?? {}
  const [name, setName] = useState('')
  const [parent, setParent] = useState<Id>(0)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    const n = cleanName(name)
    if (!n) {
      setError(`이름은 1~${NAME_MAX}자이고 '>'를 쓸 수 없습니다.`)
      return
    }
    setBusy(true)
    setError(null)
    try {
      await engine.addItem(cur.workbook.id, role, n, role === 'category' ? parent : 0)
      setName('')
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="t-add-item" onSubmit={(e) => void submit(e)}>
      <Field label="새 항목 이름">
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={NAME_MAX} />
      </Field>
      {role === 'category' && (
        <Field label="위 분류">
          <select value={parent} onChange={(e) => setParent(Number(e.target.value))}>
            <option value={0}>(맨 위)</option>
            {itemsOf(items, 'category').map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </Field>
      )}
      <ErrorText text={error} />
      <button className="secondary" type="submit" disabled={busy || name.trim() === ''}>
        더하기
      </button>
    </form>
  )
}

export function ItemsScreen({ cur }: { cur: Current }) {
  const [tab, setTab] = useState<BuiltinRole | Id>('category')
  const [editing, setEditing] = useState<TensorItem | null>(null)
  const [making, setMaking] = useState(false)
  const items = cur.book?.items ?? {}
  const dims = customDims(cur.book?.dims)
  const dim = typeof tab === 'number' ? dims.find((d) => d.id === tab) : undefined
  const role: BuiltinRole = typeof tab === 'number' ? 'category' : tab
  const tops = itemsOf(items, role)
  const tabs = (
    <div className="tabs t-dim-tabs" role="tablist" aria-label="차원">
      {ROLES.map((r) => (
        <button key={r.role} type="button" role="tab" aria-selected={tab === r.role} onClick={() => setTab(r.role)}>
          {r.label}
        </button>
      ))}
      {dims.map((d) => (
        <button key={d.id} type="button" role="tab" aria-selected={tab === d.id} onClick={() => setTab(d.id)}>
          {d.name}
        </button>
      ))}
      <button type="button" className="t-new-dim" onClick={() => setMaking(true)}>
        + 차원
      </button>
    </div>
  )
  const newDim = <NewDimSheet cur={cur} open={making} onClose={() => setMaking(false)} onMade={(id) => setTab(id)} />
  if (typeof tab === 'number')
    return (
      <div className="page t-page">
        {tabs}
        {dim ? <DimPanel key={dim.id} cur={cur} dim={dim} /> : <p className="muted empty-note">지워진 차원입니다.</p>}
        {newDim}
      </div>
    )
  return (
    <div className="page t-page">
      {tabs}
      <section className="card">
        <AddItem key={role} cur={cur} role={role} />
      </section>
      <section className="card">
        <ul className="list t-items">
          {tops.map((t, i) => {
            const children = role === 'category' ? itemsOf(items, role, t.id) : []
            return (
              <li key={t.id} className="t-item-group">
                <ul className="list">
                  <ItemRow cur={cur} item={t} index={i} count={tops.length} onEdit={setEditing} />
                  {children.map((c, j) => (
                    <ItemRow key={c.id} cur={cur} item={c} index={j} count={children.length} onEdit={setEditing} sub />
                  ))}
                </ul>
              </li>
            )
          })}
        </ul>
        {tops.length === 0 && <p className="muted">항목이 없습니다.</p>}
      </section>
      <ItemSheet cur={cur} item={editing} onClose={() => setEditing(null)} />
      {newDim}
    </div>
  )
}
